/**
 * Add-to-Home-Screen walkthrough for iOS Safari (Path B in the PWA UX plan).
 *
 * Apple does not surface a programmatic install prompt on iOS, so we coach
 * the user through the share sheet manually. The animation hint moves toward
 * the bottom toolbar on iPhone Safari and toward the top on iPad.
 */

import { motion } from 'framer-motion'
import { ArrowDown, ArrowUp, Plus, Share } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { usePromptMemory } from '@/hooks/pwa'

interface IosInstallSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function IosInstallSheet({ open, onOpenChange }: IosInstallSheetProps) {
  const memory = usePromptMemory('ios-walkthrough', 14)
  const isIPad = typeof navigator !== 'undefined' && /iPad/.test(navigator.userAgent)
  const ArrowIcon = isIPad ? ArrowUp : ArrowDown

  const handleClose = () => {
    memory.dismiss()
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-0 px-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] pt-6"
      >
        <SheetHeader className="text-left">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Plus className="h-6 w-6" />
          </div>
          <SheetTitle>Add Taqat to your home screen</SheetTitle>
          <SheetDescription>
            Three quick steps in Safari and Taqat opens like a native app.
          </SheetDescription>
        </SheetHeader>

        <ol className="mt-5 space-y-4">
          <Step
            number={1}
            label={
              <>
                Tap <Share className="mx-1 inline h-4 w-4 align-text-bottom text-primary" /> in the Safari toolbar
              </>
            }
            hint={
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 1.4 }}
                className="text-primary"
              >
                <ArrowIcon className="h-5 w-5" />
              </motion.div>
            }
          />
          <Step number={2} label={<>Scroll and tap <span className="font-semibold">Add to Home Screen</span></>} />
          <Step number={3} label={<>Tap <span className="font-semibold">Add</span> in the top right</>} />
        </ol>

        <Button onClick={handleClose} className="mt-6 w-full">
          Got it
        </Button>
      </SheetContent>
    </Sheet>
  )
}

function Step({ number, label, hint }: { number: number; label: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {number}
      </span>
      <span className="flex-1 text-sm text-foreground">{label}</span>
      {hint}
    </li>
  )
}
